import { useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useReactToPrint } from 'react-to-print';
import { PageHeader, StatusChip } from '@/components/shared';
import useRoles from '@/hooks/useRoles';
import financeClientsService, {
  ClientProfitability,
  ClientTeamMember,
} from '@/services/financeClients';
import TrioButton from '@/trio-ui/Button';
import MetricCard from '@/trio-ui/MetricCard';
import SectionCard from '@/trio-ui/SectionCard';
import type { DateFilter, DatePreset } from './FinanceXClients.container';
import FinanceXClientsPrint from './FinanceXClients.print';

type Props = {
  clients: ClientProfitability[];
  unattributed: { total: number; clients: Array<{ clientName: string; amount: number; invoices: number }> };
  isLoading: boolean;
  totals: { revenue: number; cost: number; profit: number; margin: number };
  dateFilter: DateFilter;
  onPresetChange: (preset: DatePreset) => void;
  onCustomDateChange: (startDate: string, endDate: string) => void;
  editingMember: (ClientTeamMember & { teamName: string }) | null;
  onEditMember: (member: ClientTeamMember, teamName: string) => void;
  onCloseEditMember: () => void;
  onUpdateRate: (
    memberId: string,
    monthlyRate: number | null,
    clientRate: number | null,
    clientRateType: 'hourly' | 'monthly',
    role: string | null,
    allocationPct: number,
    memberStartDate: string | null,
    memberEndDate: string | null
  ) => Promise<void>;
  onRemoveMember: (memberId: string) => Promise<void>;
  onCreateClient: (name: string) => Promise<void>;
  onAddMember: (teamId: string, profileId: string) => Promise<void>;
};

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);

function getProfitColor(profit: number | null): string | undefined {
  if (profit == null) return undefined;
  return profit >= 0 ? '#10B981' : '#EF4444';
}

function getMarginColor(margin: number | null): string | undefined {
  if (margin == null) return undefined;
  if (margin >= 30) return '#10B981';
  if (margin >= 0) return '#F59E0B';
  return '#EF4444';
}

function getStatusLabel(margin: number): string {
  if (margin >= 40) return 'high';
  if (margin >= 20) return 'medium';
  if (margin > 0) return 'low';
  return 'negative';
}

const FinanceXClientsComponent = function ({
  clients,
  unattributed,
  isLoading,
  totals,
  dateFilter,
  onPresetChange,
  onCustomDateChange,
  editingMember,
  onEditMember,
  onCloseEditMember,
  onUpdateRate,
  onRemoveMember,
  onCreateClient,
  onAddMember,
}: Props): JSX.Element {
  const { roleNames } = useRoles();

  // Print/PDF export. react-to-print is already a project dependency (see
  // ExportButton); it opens the browser print dialog, where "Save as PDF" is
  // the standard route to a shareable file — no extra dependency needed.
  const printRef = useRef<HTMLDivElement>(null);
  const handleDownloadPdf = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Client Profitability ${dateFilter.startDate} to ${dateFilter.endDate}`,
  });

  // Edit rate dialog state
  const [editRate, setEditRate] = useState('');
  const [editClientRate, setEditClientRate] = useState('');
  const [editClientRateType, setEditClientRateType] = useState<'hourly' | 'monthly'>('hourly');
  const [editRole, setEditRole] = useState('');
  const [editAllocation, setEditAllocation] = useState('100');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // New client dialog state
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Add member dialog state
  type SearchProfile = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    developerType: string | null;
  };
  const [addMemberTarget, setAddMemberTarget] = useState<{
    teamId: string;
    teamName: string;
  } | null>(null);
  const [addSearchResults, setAddSearchResults] = useState<SearchProfile[]>([]);
  const [addSelectedProfile, setAddSelectedProfile] = useState<SearchProfile | null>(null);
  const [addSearchLoading, setAddSearchLoading] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);

  const openEdit = (member: ClientTeamMember, teamName: string): void => {
    setEditRate(member.monthlyRate != null ? String(member.monthlyRate) : '');
    setEditClientRate(member.clientRate != null ? String(member.clientRate) : '');
    setEditClientRateType(member.clientRateType ?? 'hourly');
    setEditRole(member.role ?? '');
    setEditAllocation(String(member.allocationPct));
    setEditStartDate(member.startDate ?? '');
    setEditEndDate(member.endDate ?? '');
    onEditMember(member, teamName);
  };

  const handleSave = async (): Promise<void> => {
    if (!editingMember) return;
    setIsSaving(true);
    try {
      await onUpdateRate(
        editingMember.id,
        editRate ? Number(editRate) : null,
        editClientRate ? Number(editClientRate) : null,
        editClientRateType,
        editRole || null,
        Number(editAllocation) || 100,
        editStartDate || null,
        editEndDate || null
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateClient = async (): Promise<void> => {
    if (!newClientName.trim()) return;
    setIsCreating(true);
    try {
      await onCreateClient(newClientName.trim());
      setNewClientName('');
      setShowNewClient(false);
    } catch {
      // Dialog stays open so user can retry; toast is shown by the container
    } finally {
      setIsCreating(false);
    }
  };

  const maxRevenue = Math.max(...clients.map((c) => c.revenue), 1);

  const missingRates = clients.some((c) => c.members.some((m) => m.monthlyRate == null));

  return (
    <>
      {/* Rendered always but hidden by its own @media print rule, so the ref is
          live when the print dialog opens. */}
      <FinanceXClientsPrint
        ref={printRef}
        clients={clients}
        totals={totals}
        periodStart={dateFilter.startDate}
        periodEnd={dateFilter.endDate}
      />

      <PageHeader
        title="Client Profitability"
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TrioButton variantColor="blue" size="small" onClick={() => setShowNewClient(true)}>
              <AddIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Add Client
            </TrioButton>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={() => handleDownloadPdf?.()}
              disabled={clients.length === 0}
              sx={{ borderRadius: '999px', textTransform: 'none' }}
            >
              Download PDF
            </Button>
            <ToggleButtonGroup
              value={dateFilter.preset}
              exclusive
              onChange={(_, val) => val && onPresetChange(val)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: 13,
                  px: 1.5,
                  py: 0.5,
                  borderColor: '#E4E4E7',
                  '&.Mui-selected': {
                    backgroundColor: '#054AD915',
                    color: '#054AD9',
                    borderColor: '#054AD9',
                    '&:hover': { backgroundColor: '#054AD925' },
                  },
                },
              }}
            >
              <ToggleButton value="this_month">This Month</ToggleButton>
              <ToggleButton value="last_month">Last Month</ToggleButton>
              <ToggleButton value="this_quarter">This Quarter</ToggleButton>
              <ToggleButton value="this_year">YTD</ToggleButton>
              <ToggleButton value="last_year">Last Year</ToggleButton>
              <ToggleButton value="custom">Custom</ToggleButton>
            </ToggleButtonGroup>
            {dateFilter.preset === 'custom' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  type="date"
                  size="small"
                  value={dateFilter.startDate}
                  onChange={(e) => onCustomDateChange(e.target.value, dateFilter.endDate)}
                  sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
                />
                <TextField
                  type="date"
                  size="small"
                  value={dateFilter.endDate}
                  onChange={(e) => onCustomDateChange(dateFilter.startDate, e.target.value)}
                  sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
                />
              </Box>
            )}
            <Tooltip
              title="This report shows profitability per client: Revenue (invoices received) minus Allocated Cost (team salaries × allocation % × months active in the period). Use it to identify which clients are profitable and which ones need rate adjustments."
              arrow
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: { maxWidth: 320, fontSize: 12, lineHeight: 1.5, p: 1.5 },
                },
              }}
            >
              <IconButton size="small" sx={{ color: '#71717A' }}>
                <HelpOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      {/* KPI Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <MetricCard label="Total Revenue" value={formatCurrency(totals.revenue)} color="#10B981" />
        <MetricCard label="Allocated Cost" value={formatCurrency(totals.cost)} color="#EF4444" />
        <MetricCard
          label="Gross Profit"
          value={formatCurrency(totals.profit)}
          color={totals.profit >= 0 ? '#054AD9' : '#EF4444'}
        />
        <MetricCard
          label="Avg Margin"
          value={`${totals.margin}%`}
          color={totals.margin >= 30 ? '#10B981' : '#F59E0B'}
        />
      </Box>

      {/* The whole page is now a projection. Saying so once, at the top, is the
          difference between a planning tool and a report someone mistakes for
          the books. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 2,
          mb: 2,
          borderRadius: '8px',
          backgroundColor: '#EFF6FF',
          border: '1px solid #BFDBFE',
        }}
      >
        <HelpOutlineIcon sx={{ color: '#054AD9', fontSize: 20, flexShrink: 0 }} />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Revenue on this page is estimated from contracted client rates, not from invoices.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Hourly rates assume a 160-hour month, prorated by each person&apos;s days on the team.
            These figures show what an engagement is worth at contracted rates — they will not match
            what was actually billed. For invoiced revenue see the Invoices page or the
            dashboard&apos;s Monthly Breakdown.
          </Typography>
        </Box>
      </Box>

      {missingRates && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            mb: 2,
            borderRadius: '8px',
            backgroundColor: '#FEF3C7',
            border: '1px solid #F59E0B',
          }}
        >
          <WarningAmberIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
          <Typography variant="body2">
            Some team members don&apos;t have a monthly rate set. Set rates to see accurate cost and
            profit calculations.
          </Typography>
        </Box>
      )}

      {/* Revenue that reaches no client. This is the failure mode that made
          VolAI and Flyreel look unprofitable — an invoice whose vendor has no
          team mapping is simply dropped, and the client card then shows cost
          with no income. Red rather than amber: unlike a missing rate, this
          actively misstates profit. */}
      {unattributed.total > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            p: 2,
            mb: 2,
            borderRadius: '8px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
          }}
        >
          <WarningAmberIcon sx={{ color: '#EF4444', fontSize: 20, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatCurrency(unattributed.total)} of invoiced revenue isn&apos;t linked to any
              client. This page estimates from rate cards so it is unaffected, but the figure is
              missing from the dashboard&apos;s Monthly Breakdown and from revenue by client.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {unattributed.clients
                .slice(0, 4)
                .map((c) => `${c.clientName} (${formatCurrency(c.amount)})`)
                .join(' · ')}
              {unattributed.clients.length > 4 ? ` · +${unattributed.clients.length - 4} more` : ''}
              {' — '}link the vendor to a team in FinanceX → Vendors to include it.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Client Cards */}
      {isLoading && (
        <Box sx={{ py: 4 }}>
          <LinearProgress />
        </Box>
      )}

      {clients.map((client) => {
        const hasUnsetRates = client.members.some((m) => m.monthlyRate == null);

        return (
          <SectionCard
            key={client.teamId}
            title={client.teamName}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {client.category && (
                  <Chip
                    label={client.category}
                    size="small"
                    sx={{ fontSize: 11, textTransform: 'capitalize' }}
                  />
                )}
                <StatusChip
                  status={getStatusLabel(client.margin)}
                  statusMap={{
                    high: { label: `${client.margin}% margin`, color: '#10B981' },
                    medium: { label: `${client.margin}% margin`, color: '#F59E0B' },
                    low: { label: `${client.margin}% margin`, color: '#EF4444' },
                    negative: { label: `${client.margin}% margin`, color: '#EF4444' },
                  }}
                />
              </Box>
            }
          >
            {/* Revenue / Cost / Profit summary */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
                mb: 2,
                p: 2,
                borderRadius: '8px',
                backgroundColor: '#F4F4F5',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Revenue
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#10B981' }}>
                  {formatCurrency(client.revenue)}
                </Typography>
                {/* Say what these actually are. Revenue here is normally
                    accrual — invoices raised, which may well be unpaid — so
                    "payments" was wrong for every client with open AR. Only the
                    bank-transaction fallback is genuinely money received. */}
                <Typography variant="caption" color="text.secondary">
                  {client.revenueSource === 'rate_card' ? (
                    <>
                      estimated · {client.revenueTransactionCount} rate
                      {client.revenueTransactionCount !== 1 ? 's' : ''}
                    </>
                  ) : (
                    <>
                      {client.revenueTransactionCount}{' '}
                      {client.revenueSource === 'transaction' ? 'payment' : 'invoice'}
                      {client.revenueTransactionCount !== 1 ? 's' : ''}
                    </>
                  )}
                </Typography>
              </Box>
              <Box>
                {/* "Allocated" = prorated by days actually on the team in the
                    selected period, which is why it rarely equals monthly rate
                    x months. */}
                <Typography variant="caption" color="text.secondary">
                  Allocated Cost
                  <Tooltip
                    title="Monthly salary x allocation %, prorated by the days this person was on the team within the selected period. Someone who joined mid-period contributes only part of a month."
                    arrow
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: 12, ml: 0.4, verticalAlign: 'text-top', color: '#A1A1AA' }}
                    />
                  </Tooltip>
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#EF4444' }}>
                  {formatCurrency(client.allocatedCost)}
                  {hasUnsetRates && <span style={{ fontSize: 12, color: '#F59E0B' }}> *</span>}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {client.members.length} member{client.members.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Gross Profit
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: client.profit >= 0 ? '#054AD9' : '#EF4444' }}
                >
                  {formatCurrency(client.profit)}
                  {hasUnsetRates && <span style={{ fontSize: 12, color: '#F59E0B' }}> *</span>}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: client.margin >= 30 ? '#10B981' : '#F59E0B' }}
                >
                  {client.revenue > 0 ? `${client.margin}% margin` : '—'}
                </Typography>
              </Box>
            </Box>

            {/* Revenue bar */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Revenue share
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {totals.revenue > 0 ? Math.round((client.revenue / totals.revenue) * 100) : 0}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(client.revenue / maxRevenue) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#F4F4F5',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: '#054AD9',
                  },
                }}
              />
            </Box>

            {/* Team Members */}
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              {/* The card above is ACTUALS for the selected period; the rows below
                  are a full-month run-rate from the rate card. They routinely
                  disagree in sign — a member who joined mid-period shows a
                  healthy monthly margin while the card shows a loss — so each
                  side has to say what it is. */}
              <Typography variant="caption" sx={{ fontWeight: 500, color: '#71717A' }}>
                Team Members — monthly run-rate at full allocation
                <Tooltip
                  title="These columns are a projection from the rate card: a full month at 160 hours, ignoring start and end dates. The figures at the top of the card are actuals for the selected period, prorated by days on the team, so the two will differ for anyone who did not work the whole period."
                  arrow
                >
                  <HelpOutlineIcon
                    sx={{ fontSize: 13, ml: 0.5, verticalAlign: 'text-top', color: '#A1A1AA' }}
                  />
                </Tooltip>
              </Typography>
              <Button
                size="small"
                startIcon={<PersonAddIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  setAddMemberTarget({ teamId: client.teamId, teamName: client.teamName });
                  setAddSelectedProfile(null);
                  setAddSearchResults([]);
                }}
                sx={{ textTransform: 'none', fontSize: 12, color: '#054AD9' }}
              >
                Add Member
              </Button>
            </Box>
            <Box
              sx={{
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Box
                component="table"
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 13,
                  minWidth: 700,
                  '& th': {
                    textAlign: 'left',
                    padding: '8px 12px',
                    color: '#71717A',
                    fontWeight: 500,
                    fontSize: 12,
                    borderBottom: '1px solid hsla(0,0%,0%,0.08)',
                    whiteSpace: 'nowrap',
                  },
                  '& td': {
                    padding: '8px 12px',
                    borderBottom: '1px solid hsla(0,0%,0%,0.04)',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th style={{ textAlign: 'right' }}>Client Rate</th>
                    <th style={{ textAlign: 'right' }}>Mo. Revenue</th>
                    <th style={{ textAlign: 'right' }}>Mo. Salary</th>
                    <th style={{ textAlign: 'right' }}>Mo. Profit</th>
                    <th style={{ textAlign: 'center' }}>Margin</th>
                    <th style={{ width: 72 }}>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {client.members.map((member) => (
                    <tr
                      key={member.id}
                      style={{
                        opacity:
                          member.endDate && member.endDate < new Date().toISOString().split('T')[0]
                            ? 0.5
                            : 1,
                      }}
                    >
                      <td style={{ fontWeight: 500 }}>
                        {member.firstName} {member.lastName}
                        {member.endDate &&
                          member.endDate < new Date().toISOString().split('T')[0] && (
                            <span
                              style={{
                                display: 'inline-block',
                                marginLeft: 6,
                                padding: '1px 6px',
                                borderRadius: 4,
                                fontSize: 10,
                                fontWeight: 500,
                                backgroundColor: '#FEE2E2',
                                color: '#DC2626',
                                border: '1px solid #FECACA',
                                verticalAlign: 'middle',
                              }}
                            >
                              Inactive
                            </span>
                          )}
                      </td>
                      {/* Team role first. This used to read developerType ?? role,
                          so a profile with a developer_type ("AI Engineer") masked
                          the role held on THIS team ("Full Stack Developer") — and
                          editing the role in the dialog appeared to do nothing,
                          because the edit writes team_members.role. */}
                      <td style={{ color: '#71717A' }}>{member.role ?? member.developerType}</td>
                      {(() => {
                        const isHourly = member.clientRateType === 'hourly';
                        let moRevenue: number | null = null;
                        if (member.clientRate != null) {
                          moRevenue = isHourly ? member.clientRate * 160 : member.clientRate;
                        }
                        const moSalary = member.monthlyRate;
                        const moProfit =
                          moRevenue != null && moSalary != null ? moRevenue - moSalary : null;
                        const margin =
                          moRevenue != null && moRevenue > 0 && moProfit != null
                            ? Math.round((moProfit / moRevenue) * 100)
                            : null;
                        return (
                          <>
                            <td style={{ textAlign: 'right' }}>
                              {member.clientRate != null ? (
                                <span>
                                  ${member.clientRate}
                                  {isHourly ? '/hr' : '/mo'}
                                </span>
                              ) : (
                                <span style={{ color: '#9CA3AF' }}>—</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {moRevenue != null ? (
                                formatCurrency(moRevenue)
                              ) : (
                                <span style={{ color: '#9CA3AF' }}>—</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {moSalary != null ? (
                                formatCurrency(moSalary)
                              ) : (
                                <span style={{ color: '#F59E0B' }}>Not set</span>
                              )}
                            </td>
                            <td
                              style={{
                                textAlign: 'right',
                                fontWeight: 500,
                                color: getProfitColor(moProfit),
                              }}
                            >
                              {moProfit != null ? formatCurrency(moProfit) : '—'}
                            </td>
                            <td
                              style={{
                                textAlign: 'center',
                                fontWeight: 500,
                                color: getMarginColor(margin),
                              }}
                            >
                              {margin != null ? `${margin}%` : '—'}
                            </td>
                          </>
                        );
                      })()}
                      <td>
                        <Tooltip title={member.monthlyRate == null ? 'Set rates' : 'Edit rate'}>
                          <IconButton
                            size="small"
                            onClick={() => openEdit(member, client.teamName)}
                          >
                            {member.monthlyRate == null ? (
                              <WarningAmberIcon sx={{ fontSize: 16, color: '#F59E0B' }} />
                            ) : (
                              <EditIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                  {client.members.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        style={{ color: '#9CA3AF', textAlign: 'center', padding: 16 }}
                      >
                        No team members. Add members in the Teams section.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Box>
            </Box>
          </SectionCard>
        );
      })}

      {!isLoading && clients.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8, color: '#71717A' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            No client data
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add a client to start tracking profitability.
          </Typography>
          <TrioButton variantColor="blue" size="small" onClick={() => setShowNewClient(true)}>
            <AddIcon sx={{ fontSize: 18, mr: 0.5 }} />
            Add Client
          </TrioButton>
        </Box>
      )}

      {/* New Client Dialog */}
      <Dialog open={showNewClient} onClose={() => setShowNewClient(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Client</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates a client team and a matching income vendor. You can then add team members and
            their rates.
          </Typography>
          <TextField
            label="Client Name"
            size="small"
            fullWidth
            autoFocus
            value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)}
            placeholder="e.g. Acme Corp"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateClient();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowNewClient(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <TrioButton
            variantColor="blue"
            size="small"
            onClick={handleCreateClient}
            disabled={isCreating || !newClientName.trim()}
          >
            {isCreating ? 'Creating...' : 'Create Client'}
          </TrioButton>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog
        open={!!addMemberTarget}
        onClose={() => setAddMemberTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add Member — {addMemberTarget?.teamName}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Autocomplete
            options={addSearchResults}
            getOptionLabel={(p) => `${p.firstName} ${p.lastName} (${p.email})`}
            value={addSelectedProfile}
            onChange={(_, val) => setAddSelectedProfile(val)}
            onInputChange={async (_, value) => {
              if (value.length < 2) {
                setAddSearchResults([]);
                return;
              }
              setAddSearchLoading(true);
              try {
                const results = await financeClientsService.searchProfiles(value);
                setAddSearchResults(results);
              } finally {
                setAddSearchLoading(false);
              }
            }}
            loading={addSearchLoading}
            noOptionsText="Type at least 2 characters to search..."
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField {...params} label="Search by name..." size="small" fullWidth autoFocus />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddMemberTarget(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <TrioButton
            variantColor="blue"
            size="small"
            onClick={async () => {
              if (!addMemberTarget || !addSelectedProfile) return;
              setIsAddingMember(true);
              try {
                await onAddMember(addMemberTarget.teamId, addSelectedProfile.id);
                setAddMemberTarget(null);
              } finally {
                setIsAddingMember(false);
              }
            }}
            disabled={!addSelectedProfile || isAddingMember}
          >
            {isAddingMember ? 'Adding...' : 'Add Member'}
          </TrioButton>
        </DialogActions>
      </Dialog>

      {/* Edit Rate Dialog */}
      <Dialog open={!!editingMember} onClose={onCloseEditMember} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Edit Rate — {editingMember?.firstName} {editingMember?.lastName}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Team: {editingMember?.teamName}
          </Typography>
          <TextField
            select
            label="Role"
            size="small"
            fullWidth
            value={editRole}
            onChange={(e) => setEditRole(e.target.value)}
            SelectProps={{ native: true }}
          >
            <option value="">— Select role —</option>
            {roleNames.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Client Rate (USD)"
              type="number"
              size="small"
              fullWidth
              value={editClientRate}
              onChange={(e) => setEditClientRate(e.target.value)}
              placeholder="e.g. 65"
              helperText="What the client pays for this person."
            />
            <TextField
              select
              label="Rate Type"
              size="small"
              value={editClientRateType}
              onChange={(e) => setEditClientRateType(e.target.value as 'hourly' | 'monthly')}
              sx={{ minWidth: 120 }}
              SelectProps={{ native: true }}
            >
              <option value="hourly">Hourly</option>
              <option value="monthly">Monthly</option>
            </TextField>
          </Box>
          <TextField
            label="Monthly Salary (USD)"
            type="number"
            size="small"
            fullWidth
            value={editRate}
            onChange={(e) => setEditRate(e.target.value)}
            placeholder="e.g. 5000"
            helperText="What you pay the contractor per month."
          />
          <TextField
            label="Allocation %"
            type="number"
            size="small"
            fullWidth
            value={editAllocation}
            onChange={(e) => setEditAllocation(e.target.value)}
            placeholder="100"
            inputProps={{ min: 1, max: 100 }}
            helperText="If this person works on multiple clients, set the % allocated to this one."
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              fullWidth
              value={editStartDate}
              onChange={(e) => setEditStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="When this member started on this project."
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              fullWidth
              value={editEndDate}
              onChange={(e) => setEditEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty if still active."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              if (!editingMember) return;
              const { id } = editingMember;
              onCloseEditMember();
              onRemoveMember(id);
            }}
            sx={{
              textTransform: 'none',
              color: '#DC2626',
              '&:hover': { backgroundColor: '#FEF2F2' },
            }}
          >
            Remove from team
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onCloseEditMember} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <TrioButton variantColor="blue" size="small" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </TrioButton>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FinanceXClientsComponent;
